import React from 'react';

import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';
import { Icon } from 'components/base/Icon';

type StepStatus = 'done' | 'loading';

type Step = {
  key: string;
  labelKey: string;
  status: StepStatus;
  // Only rendered as part of the registration flow.
  registerOnly?: boolean;
};

interface Props {
  isRegister: boolean;
}

// On entering this screen the profile is already created, so only the
// organising step is still running.
//
// Built per render rather than held in a module-level const: `translations` is
// an empty object until the i18n init callback fills it, and this module is
// imported at app start (index.tsx -> App -> routes -> RouteLoading) well
// before i18n() runs. Reading it at module scope captured `undefined` keys, so
// the steps rendered with no label at all.
const buildSteps = (): Step[] => [
  {
    key: 'profile-created',
    labelKey: translations.ProfileCreated,
    status: 'done',
    registerOnly: true,
  },
  { key: 'organising', labelKey: translations.Organising, status: 'loading' },
];

const StepIcon = ({ status }: { status: StepStatus }): React.JSX.Element => (
  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-stone-100 sm:h-16 sm:w-16">
    {status === 'done' ? (
      <div className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-[#007855] sm:h-11 sm:w-11">
        <Icon name="check" size={28} fill="#007855" />
      </div>
    ) : (
      <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-zinc-100 border-t-primary sm:h-11 sm:w-11" />
    )}
  </div>
);

const LoadingProfile = ({ isRegister }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const steps = buildSteps().filter((step) => isRegister || !step.registerOnly);

  return (
    <div className="flex min-h-screen w-full justify-center overflow-x-hidden bg-white">
      <div className="w-full max-w-[1494px] px-6 pt-16 sm:px-10 sm:pt-24 lg:px-16 xl:pl-[379px] xl:pt-[220px]">
        <div className="w-full max-w-[735px]">
          <h1 className="font-sans text-5xl font-normal leading-tight text-primary sm:text-5xl">
            {t(translations.AlmostReady)}
          </h1>
          <p className="mt-4 font-sans text-lg font-medium leading-7 text-primary sm:text-lg">
            {t(translations.DoNotCloseOrRefresh)}
          </p>

          <div
            role="status"
            aria-live="polite"
            className="mt-10 flex flex-col gap-6 sm:mt-14 sm:gap-8"
          >
            {steps.map((step) => (
              <div key={step.key} className="flex items-center gap-4 sm:gap-5">
                <StepIcon status={step.status} />
                <span
                  className={`font-sans text-xl text-primary sm:text-xl ${
                    step.status === 'loading' ? 'font-medium' : 'font-normal'
                  }`}
                >
                  {t(step.labelKey)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingProfile;
